import { useState, useCallback } from 'react';
import { Link, useLocation } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Button, Card, Checkbox, Field, Input, Logo } from '../components/primitives/index.js';
import { TurnstileWidget } from '../components/composites/index.js';

export function Login() {
  const [location] = useLocation();
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get('redirect') ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [gdpr, setGdpr] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const onTurnstile = useCallback((t: string) => setTurnstileToken(t), []);

  const send = useMutation({
    mutationFn: () =>
      api.requestMagicLink({
        email: email.trim(),
        redirect,
        marketing_consent: marketing,
        turnstile_token: turnstileToken ?? undefined,
      }),
  });

  if (send.isSuccess) {
    return (
      <Shell>
        <Card className="max-w-md mx-auto text-center">
          <div className="text-4xl mb-4">📬</div>
          <h1 className="text-2xl font-medium mb-2">Verifică-ți emailul</h1>
          <p className="text-sm text-muted leading-relaxed">
            Ți-am trimis un link de autentificare pe <strong className="text-fg">{email}</strong>.
            Click pe link în următoarele 15 minute.
          </p>
          {send.data?.dev_verify_url && (
            <div className="mt-6 p-4 bg-warn-soft border border-warn/40 rounded-lg text-left">
              <div className="font-mono text-xs text-warn-text mb-2">DEV · Resend nu e configurat</div>
              <a
                href={send.data.dev_verify_url}
                className="text-xs break-all text-fg underline"
              >
                {send.data.dev_verify_url}
              </a>
            </div>
          )}
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card className="max-w-md mx-auto">
        <h1 className="text-2xl font-medium mb-1">Autentificare</h1>
        <p className="text-sm text-muted mb-6">
          Îți trimitem un link pe email — fără parolă.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (gdpr && email.trim()) send.mutate();
          }}
          className="flex flex-col gap-4"
        >
          <Field label="Email">
            <Input
              type="email"
              required
              placeholder="adresa@email.ro"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
            />
          </Field>
          <Checkbox
            required
            checked={gdpr}
            onChange={(e) => setGdpr(e.target.checked)}
            label={
              <>
                Sunt de acord cu <Link href="/terms" className="underline">termenii</Link> și{' '}
                <Link href="/privacy" className="underline">politica de confidențialitate</Link>{' '}
                (GDPR).
              </>
            }
          />
          <Checkbox
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            label="Trimiteți-mi rar email-uri cu spețe câștigate și actualizări juridice. (Opțional)"
          />
          <TurnstileWidget onToken={onTurnstile} />
          {send.isError && (
            <div className="text-xs text-danger">A apărut o problemă. Mai încearcă o dată.</div>
          )}
          <Button
            type="submit"
            disabled={!gdpr || !email.trim() || !turnstileToken || send.isPending}
            size="lg"
            pill
            icon={null}
          >
            {send.isPending ? 'Se trimite…' : 'Trimite link de autentificare'}
          </Button>
        </form>
      </Card>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg flex flex-col">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line bg-bg/85 backdrop-blur-xl">
        <Logo />
      </nav>
      <main className="flex-1 px-4 md:px-6 py-10 md:py-16 flex items-center">
        <div className="max-w-md w-full mx-auto">{children}</div>
      </main>
    </div>
  );
}
